import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Info, ChevronDown, ChevronUp } from "lucide-react";

const SIGNS = [
  "Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo",
  "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces"
];

const SIGN_SYMBOLS = {
  Aries: "♈", Taurus: "♉", Gemini: "♊", Cancer: "♋",
  Leo: "♌", Virgo: "♍", Libra: "♎", Scorpio: "♏",
  Sagittarius: "♐", Capricorn: "♑", Aquarius: "♒", Pisces: "♓"
};

const SIGN_COLORS = {
  Aries: "#EF4444", Taurus: "#22C55E", Gemini: "#F59E0B", Cancer: "#94A3B8",
  Leo: "#F97316", Virgo: "#10B981", Libra: "#EC4899", Scorpio: "#8B5CF6",
  Sagittarius: "#F59E0B", Capricorn: "#6B7280", Aquarius: "#3B82F6", Pisces: "#06B6D4"
};

const NAKSHATRA_LIST = [
  "Ashwini", "Bharani", "Krittika", "Rohini", "Mrigashira", "Ardra",
  "Punarvasu", "Pushya", "Ashlesha", "Magha", "Purva Phalguni", "Uttara Phalguni",
  "Hasta", "Chitra", "Swati", "Vishakha", "Anuradha", "Jyeshtha",
  "Mula", "Purva Ashadha", "Uttara Ashadha", "Shravana", "Dhanishta", "Shatabhisha",
  "Purva Bhadrapada", "Uttara Bhadrapada", "Revati"
];

// SVG Birth Chart Wheel Component
function ChartWheel({ tropicalSign, tropicalDegree, siderealLongitude, nakshatra, pada }) {
  const tropicalSignIndex = SIGNS.indexOf(tropicalSign);
  const tropicalTotalDegree = tropicalSignIndex * 30 + tropicalDegree;
  
  // Calculate nakshatra position (sidereal)
  const nakshatraIndex = NAKSHATRA_LIST.indexOf(nakshatra);
  const nakshatraSpan = 360 / 27; // 13.333°
  const nakshatraCenterDegree = (nakshatraIndex * nakshatraSpan) + (nakshatraSpan / 2);
  
  // Convert to SVG coordinates (0° = right, rotating counter-clockwise like astro charts)
  const tropicalAngle = (90 - tropicalTotalDegree) * (Math.PI / 180);
  const siderealAngle = (90 - siderealLongitude) * (Math.PI / 180);
  
  const centerX = 150;
  const centerY = 150;
  const outerRadius = 130;
  const middleRadius = 100;
  const innerRadius = 70;
  const moonRadius = 45;
  
  // Calculate Moon marker positions
  const tropicalMoonX = centerX + Math.cos(tropicalAngle) * (middleRadius + 15);
  const tropicalMoonY = centerY - Math.sin(tropicalAngle) * (middleRadius + 15);
  const siderealMoonX = centerX + Math.cos(siderealAngle) * innerRadius;
  const siderealMoonY = centerY - Math.sin(siderealAngle) * innerRadius;
  
  return (
    <svg viewBox="0 0 300 300" className="w-full max-w-[300px] mx-auto">
      <defs>
        {/* Gradient backgrounds */}
        <radialGradient id="chartBg">
          <stop offset="0%" stopColor="rgba(255,255,255,0.02)" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
        
        {/* Glow effect for Moon */}
        <filter id="moonGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      
      {/* Background */}
      <circle cx={centerX} cy={centerY} r={outerRadius} fill="url(#chartBg)" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
      
      {/* Outer ring - Tropical Signs (12 divisions) */}
      {SIGNS.map((sign, i) => {
        const startAngle = ((90 - i * 30) * Math.PI) / 180;
        const endAngle = ((90 - (i + 1) * 30) * Math.PI) / 180;
        const x1 = centerX + Math.cos(startAngle) * outerRadius;
        const y1 = centerY - Math.sin(startAngle) * outerRadius;
        const x2 = centerX + Math.cos(startAngle) * middleRadius;
        const y2 = centerY - Math.sin(startAngle) * middleRadius;
        
        // Symbol position (middle of segment)
        const midAngle = ((90 - i * 30 - 15) * Math.PI) / 180;
        const symbolX = centerX + Math.cos(midAngle) * (outerRadius - 15);
        const symbolY = centerY - Math.sin(midAngle) * (outerRadius - 15);
        
        const isActive = sign === tropicalSign;
        
        return (
          <g key={sign}>
            {/* Segment divider */}
            <line
              x1={x1} y1={y1} x2={x2} y2={y2}
              stroke="rgba(255,255,255,0.1)"
              strokeWidth="1"
            />
            {/* Sign symbol */}
            <text
              x={symbolX}
              y={symbolY}
              textAnchor="middle"
              dominantBaseline="middle"
              className="text-xs"
              fill={isActive ? SIGN_COLORS[sign] : "rgba(255,255,255,0.3)"}
              style={{ fontSize: isActive ? "14px" : "11px", fontWeight: isActive ? "bold" : "normal" }}
            >
              {SIGN_SYMBOLS[sign]}
            </text>
          </g>
        );
      })}
      
      {/* Middle ring divider */}
      <circle cx={centerX} cy={centerY} r={middleRadius} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
      
      {/* Inner ring - Nakshatra indicator */}
      <circle cx={centerX} cy={centerY} r={innerRadius} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1" strokeDasharray="2,4" />
      
      {/* Tropical Moon marker (outer) */}
      <circle
        cx={tropicalMoonX}
        cy={tropicalMoonY}
        r="8"
        fill={SIGN_COLORS[tropicalSign]}
        filter="url(#moonGlow)"
        opacity="0.9"
      />
      <text
        x={tropicalMoonX}
        y={tropicalMoonY + 1}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="white"
        style={{ fontSize: "10px" }}
      >
        ☽
      </text>
      
      {/* Line connecting tropical to sidereal positions */}
      <line
        x1={tropicalMoonX}
        y1={tropicalMoonY}
        x2={siderealMoonX}
        y2={siderealMoonY}
        stroke="rgba(255,255,255,0.15)"
        strokeWidth="1"
        strokeDasharray="3,3"
      />
      
      {/* Sidereal Moon marker (inner) */}
      <circle
        cx={siderealMoonX}
        cy={siderealMoonY}
        r="6"
        fill="rgba(251,191,36,0.8)"
        filter="url(#moonGlow)"
      />
      
      {/* Center - Nakshatra name */}
      <circle cx={centerX} cy={centerY} r={moonRadius} fill="rgba(255,255,255,0.02)" stroke="rgba(255,255,255,0.05)" />
      <text
        x={centerX}
        y={centerY - 8}
        textAnchor="middle"
        fill="rgba(255,255,255,0.7)"
        style={{ fontSize: "9px", letterSpacing: "0.5px" }}
      >
        {nakshatra}
      </text>
      <text
        x={centerX}
        y={centerY + 8}
        textAnchor="middle"
        fill="rgba(255,255,255,0.4)"
        style={{ fontSize: "8px" }}
      >
        Pada {pada}
      </text>
      
      {/* Legend */}
      <g transform="translate(10, 270)">
        <circle cx="5" cy="5" r="4" fill={SIGN_COLORS[tropicalSign]} />
        <text x="14" y="8" fill="rgba(255,255,255,0.5)" style={{ fontSize: "8px" }}>Tropical</text>
        <circle cx="55" cy="5" r="4" fill="rgba(251,191,36,0.8)" />
        <text x="64" y="8" fill="rgba(255,255,255,0.5)" style={{ fontSize: "8px" }}>Sidereal</text>
      </g>
    </svg>
  );
}

// Explanation Panel Component
function SystemExplanation({ tropicalSign, nakshatra, isExpanded, onToggle }) {
  return (
    <div className="rounded-lg overflow-hidden" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-white/[0.02] transition-colors"
        data-testid="system-explanation-toggle"
      >
        <div className="flex items-center gap-2">
          <Info className="w-4 h-4 text-amber-500/70" />
          <span className="text-white/60 text-sm">Why might my Moon sign look different?</span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-white/40" />
        ) : (
          <ChevronDown className="w-4 h-4 text-white/40" />
        )}
      </button>
      
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 space-y-4">
              {/* Summary */}
              <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/10">
                <p className="text-white/70 text-sm leading-relaxed">
                  <strong className="text-amber-400">Your Moon is in tropical {tropicalSign}</strong> and sidereal <strong className="text-amber-400">{nakshatra}</strong>. 
                  These aren't contradictory — they're two different coordinate systems measuring the same Moon.
                </p>
              </div>
              
              {/* Two Systems Explained */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="p-3 rounded-lg" style={{ background: "rgba(255,255,255,0.02)" }}>
                  <p className="text-white/50 text-[10px] tracking-[0.15em] uppercase mb-2">Tropical Zodiac (Signs)</p>
                  <p className="text-white/60 text-xs leading-relaxed">
                    Based on Earth's seasons. 0° Aries = spring equinox in the Northern Hemisphere. 
                    This is what Western astrology typically uses for "Sun sign" and "Moon sign."
                  </p>
                </div>
                <div className="p-3 rounded-lg" style={{ background: "rgba(255,255,255,0.02)" }}>
                  <p className="text-white/50 text-[10px] tracking-[0.15em] uppercase mb-2">Sidereal Nakshatras</p>
                  <p className="text-white/60 text-xs leading-relaxed">
                    Based on the fixed stars. We use True Lahiri (True Chitra), anchored to the star Spica. 
                    This is traditional Vedic astrology's nakshatra system.
                  </p>
                </div>
              </div>
              
              {/* The ~24° Difference */}
              <div className="p-3 rounded-lg" style={{ background: "rgba(59,130,246,0.05)", border: "1px solid rgba(59,130,246,0.1)" }}>
                <p className="text-white/50 text-[10px] tracking-[0.15em] uppercase mb-2">The ~24° Difference</p>
                <p className="text-white/60 text-xs leading-relaxed">
                  Due to Earth's axial wobble (precession), the tropical and sidereal zodiacs have drifted apart by about 24° over millennia. 
                  This is called the <em>ayanamsa</em>. We calculate it precisely for your birth moment using the True Chitra method.
                </p>
              </div>
              
              {/* Why We Use Both */}
              <div>
                <p className="text-white/50 text-[10px] tracking-[0.15em] uppercase mb-2">Why We Use Both</p>
                <ul className="space-y-1.5 text-white/60 text-xs">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500/70 mt-0.5">•</span>
                    <span><strong className="text-white/70">Tropical signs</strong> connect to seasonal/psychological archetypes.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500/70 mt-0.5">•</span>
                    <span><strong className="text-white/70">Sidereal nakshatras</strong> preserve star-based Vedic techniques (dasha periods, compatibility).</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500/70 mt-0.5">•</span>
                    <span>This hybrid approach is used by astrologers who value both frameworks.</span>
                  </li>
                </ul>
              </div>
              
              {/* Calculation System Badge */}
              <div className="flex items-center gap-2 pt-2 border-t border-white/5">
                <span className="text-white/30 text-[10px] tracking-[0.1em] uppercase">Calculation System:</span>
                <span className="text-white/50 text-[10px] bg-white/5 px-2 py-0.5 rounded">
                  Tropical zodiac · Sidereal nakshatras · True Lahiri
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Main Export: Birth Chart Visual with Explanation
export default function BirthChartVisual({ result }) {
  const [showExplanation, setShowExplanation] = useState(false);
  
  if (!result) return null;
  
  // Extract data from result (handle both old and new API formats)
  const tropicalSign = result.tropical_sign || result.sidereal_sign;
  const tropicalDegree = result.tropical_degree_in_sign || result.degree_in_sign || 0;
  const siderealLongitude = result.sidereal_longitude || (result.degree_in_sign + (SIGNS.indexOf(result.sidereal_sign) * 30));
  const nakshatra = result.nakshatra_name;
  const pada = result.pada;
  
  return (
    <div className="space-y-4" data-testid="birth-chart-visual">
      {/* Chart Wheel */}
      <div className="rounded-lg p-4 sm:p-6" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
        <p className="text-white/40 text-xs tracking-[0.15em] uppercase mb-4 text-center">Birth Chart</p>
        
        <ChartWheel
          tropicalSign={tropicalSign}
          tropicalDegree={tropicalDegree}
          siderealLongitude={siderealLongitude}
          nakshatra={nakshatra}
          pada={pada}
        />
        
        {/* Position Summary */}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="p-3 rounded-lg text-center" style={{ background: `${SIGN_COLORS[tropicalSign]}10`, border: `1px solid ${SIGN_COLORS[tropicalSign]}20` }}>
            <p className="text-white/40 text-[10px] tracking-[0.1em] uppercase mb-1">Tropical Sign</p>
            <p className="text-white/90 text-lg font-light">
              {SIGN_SYMBOLS[tropicalSign]} {tropicalSign}
            </p>
            <p className="text-white/50 text-xs">{tropicalDegree.toFixed(1)}°</p>
          </div>
          <div className="p-3 rounded-lg text-center" style={{ background: "rgba(251,191,36,0.05)", border: "1px solid rgba(251,191,36,0.15)" }}>
            <p className="text-white/40 text-[10px] tracking-[0.1em] uppercase mb-1">Sidereal Nakshatra</p>
            <p className="text-white/90 text-lg font-light">
              {nakshatra}
            </p>
            <p className="text-white/50 text-xs">Pada {pada}</p>
          </div>
        </div>
      </div>
      
      {/* Explanation Panel */}
      <SystemExplanation
        tropicalSign={tropicalSign}
        nakshatra={nakshatra}
        isExpanded={showExplanation}
        onToggle={() => setShowExplanation(!showExplanation)}
      />
    </div>
  );
}

// Also export individual components for flexible usage
export { ChartWheel, SystemExplanation };
